import { StatisticsStorage } from "../storage/statistics_storage.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
// import { AnalyticsGameType } from "./analytics_types.js";

const SIGNUP_TIMESTAMP_KEY = 'analytics_signup_timestamp';
const USERID_KEY = 'analytics_userid';
const GAME_NUMBER_EASY_KEY = 'analytics_game_number_easy';
const GAME_NUMBER_HARD_KEY = 'analytics_game_number_hard';
const LEVEL_ATTEMPTS_KEY_PREFIX = 'analytics_level_attempts_';

type TelegramWebAppIdentity = {
  platform?: string;
  initDataUnsafe?: {
    user?: {
      id?: number | string;
    };
  };
};

const getCapacitorPlatform = (): unknown =>
  ( window as unknown as { Capacitor?: { getPlatform?: () => unknown } } ).Capacitor?.getPlatform?.();

const getTelegramWebApp = (): TelegramWebAppIdentity | undefined =>
  ( window as unknown as { Telegram?: { WebApp?: TelegramWebAppIdentity } } ).Telegram?.WebApp;

function getPlatform(): string
{
  try
  {
    const cap_platform = getCapacitorPlatform();
    if ( cap_platform ) return String( cap_platform );
  } catch {}

  try
  {
    const tg_platform = getTelegramWebApp()?.platform;
    if ( tg_platform ) return String( tg_platform );
  } catch {}

  return 'web';
}

export function resolveCountry(): string
{
  const lang = ( TelegramAPISettings.getUserLanguage() ?? navigator.language ?? '' ).trim();
  const m = lang.match( /[-_](?<cc>[a-zA-Z]{2})$/ );
  const cc = m?.groups?.cc;
  return cc ? cc.toUpperCase() : 'UNKNOWN';
}

function getTelegramUserId(): string | null
{
  try
  {
    const id = getTelegramWebApp()?.initDataUnsafe?.user?.id;
    if ( id === 0 ) return '0';
    if ( typeof id === 'number' && Number.isFinite( id ) ) return String( id );
    if ( typeof id === 'string' && id.length > 0 ) return id;
  } catch {}
  return null;
}

function makeAnonUserId(): string
{
  try
  {
    const any_crypto = ( globalThis as { crypto?: Crypto } ).crypto;
    const uuid = any_crypto?.randomUUID?.();
    if ( uuid ) return uuid;
  } catch {}

  return `${Date.now()}_${Math.random().toString( 16 ).slice( 2 )}`;
}

export function ensureUserId(): string
{
  const tg_user_id = getTelegramUserId();
  if ( tg_user_id ) return tg_user_id;

  const stored = localStorage.getItem( USERID_KEY );
  if ( stored && stored.length > 0 ) return stored;

  const new_id = makeAnonUserId();
  localStorage.setItem( USERID_KEY, new_id );
  return new_id;
}

export function ensureSignupTimestampIso(): { signup_timestamp_iso: string; is_first_open: boolean }
{
  const stored = localStorage.getItem( SIGNUP_TIMESTAMP_KEY );
  if ( stored && stored.length > 0 )
  {
    return { signup_timestamp_iso: stored, is_first_open: false };
  }

  const now_iso = new Date().toISOString();
  localStorage.setItem( SIGNUP_TIMESTAMP_KEY, now_iso );
  return { signup_timestamp_iso: now_iso, is_first_open: true };
}

export function getUserLevel(): number
{
  try
  {
    const lvl = StatisticsStorage.load().level;
    if ( typeof lvl === 'number' && Number.isFinite( lvl ) ) return Math.max( 1, Math.floor( lvl ) );
  } catch {}
  return 1;
}

export function getCommonAnalyticsFields(): {
  userid: string;
  signuptimestamp: string;
  platform: string;
  country: string;
  userlevel: number;
  is_first_open: boolean;
}
{
  const { signup_timestamp_iso, is_first_open } = ensureSignupTimestampIso();
  return {
    userid: ensureUserId(),
    signuptimestamp: signup_timestamp_iso,
    platform: getPlatform(),
    country: resolveCountry(),
    userlevel: getUserLevel(),
    is_first_open,
  };
}

export function nextGameNumber(): number
{
  const prev_raw = localStorage.getItem( GAME_NUMBER_EASY_KEY );
  const prev = prev_raw ? Number( prev_raw ) : 0;
  const next = Math.max( 0, Number.isFinite( prev ) ? Math.floor( prev ) : 0 ) + 1;
  localStorage.setItem( GAME_NUMBER_EASY_KEY, String( next ) );
  return next;
}

export function recordLevelAttempt( level: number ): number
{
  const normalized_level = Math.max( 1, Math.floor( level ) );
  const key = `${LEVEL_ATTEMPTS_KEY_PREFIX}${normalized_level}`;

  try
  {
    const stored = Number( localStorage.getItem( key ) );
    const attempts = ( Number.isFinite( stored ) ? Math.max( 0, Math.floor( stored ) ) : 0 ) + 1;
    localStorage.setItem( key, String( attempts ) );
    return attempts;
  }
  catch
  {
    return 1;
  }
}

export function takeLevelAttempts( level: number ): number
{
  const normalized_level = Math.max( 1, Math.floor( level ) );
  const key = `${LEVEL_ATTEMPTS_KEY_PREFIX}${normalized_level}`;

  try
  {
    const stored = Number( localStorage.getItem( key ) );
    localStorage.removeItem( key );
    return Number.isFinite( stored ) ? Math.max( 1, Math.floor( stored ) ) : 1;
  }
  catch
  {
    return 1;
  }
}

// export function nextGameNumber( game_type: AnalyticsGameType, mission_level?: number ): number
// {
//   if ( game_type === AnalyticsGameType.MISSIONS )
//   {
//     return Math.max( 1, Math.floor( mission_level ?? 1 ) );
//   }

//   const key = game_type === AnalyticsGameType.HARD ? GAME_NUMBER_HARD_KEY : GAME_NUMBER_EASY_KEY;
//   const prev_raw = localStorage.getItem( key );
//   const prev = prev_raw ? Number( prev_raw ) : 0;
//   const next = ( Number.isFinite( prev ) ? prev : 0 ) + 1;
//   localStorage.setItem( key, String( next ) );
//   return next;
// }
